import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Spinner from '../Spinner';
import { IconCheck, IconX } from '../Icons';
import type { OrganizeJob } from '../../hooks/useOrganizeJob';
import type { VersionJob } from '../../hooks/useVersionJob';

type Tone = 'busy' | 'ok' | 'warn';

const ToneColor: Record<Tone, string> = {
  busy: 'var(--text-2)',
  ok: 'var(--ok)',
  warn: 'var(--warn)',
};

interface TuningJobStatusProps {
  projectId: string;
  versionJob: VersionJob;
  organizeJob: OrganizeJob;
}

/** What the project's jobs are doing, one line each, under the Tuning panel's title. */
export default function TuningJobStatus({ projectId, versionJob, organizeJob }: TuningJobStatusProps) {
  const { t } = useTranslation();
  const version = versionJob.state;
  const organize = organizeJob.state;

  return (
    <>
      {version.phase === 'running' && (
        <StatusLine tone="busy">
          {t(version.activity === 'exposing' ? 'editor.exposing' : 'editor.versioning')}
        </StatusLine>
      )}
      {version.phase === 'ready' && (
        <StatusLine tone="ok" onDismiss={versionJob.dismiss}>
          {t('editor.versionReady', { semver: version.semver })}{' '}
          <button
            type="button"
            onClick={() => window.location.assign(`/play/${projectId}`)}
            style={{ padding: 0, border: 'none', background: 'none', color: 'var(--accent)', font: 'inherit', cursor: 'pointer', textDecoration: 'underline' }}
          >
            {t('editor.reload')}
          </button>
        </StatusLine>
      )}
      {version.phase === 'failed' && (
        <StatusLine tone="warn" onDismiss={versionJob.dismiss}>
          {version.message ?? t('editor.versionFailed')}
        </StatusLine>
      )}

      {organize.phase === 'running' && <StatusLine tone="busy">{t('editor.organizing')}</StatusLine>}
      {organize.phase === 'arranged' && <StatusLine tone="ok">{t('editor.organized')}</StatusLine>}
      {organize.phase === 'failed' && (
        <StatusLine tone="warn" onDismiss={organizeJob.dismiss}>
          {organize.message ?? t('editor.organizeJobFailed')}
        </StatusLine>
      )}
    </>
  );
}

function StatusLine({ tone, onDismiss, children }: { tone: Tone; onDismiss?: () => void; children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <div role="status" style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 11, lineHeight: 1.45, color: ToneColor[tone] }}>
      <span style={{ display: 'grid', placeItems: 'center', width: 12, height: 16, flex: 'none' }}>
        {tone === 'busy' ? <Spinner size={12} /> : tone === 'ok' ? <IconCheck size={12} /> : null}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>{children}</span>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label={t('editor.dismiss')}
          title={t('editor.dismiss')}
          style={{ display: 'grid', placeItems: 'center', height: 16, padding: 0, border: 'none', background: 'none', color: 'var(--text-5)', cursor: 'pointer', flex: 'none' }}
        >
          <IconX size={11} />
        </button>
      )}
    </div>
  );
}
